import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Chip,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { DataAndFilterContext } from '../context/DataAndFilterContext';
import BACKEND_URL from '../config';

function CustomTagModal({ open, onClose, dataFile }) {
  const { updateDataFile, filteredData } = useContext(DataAndFilterContext);

  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [localAvailableTags, setLocalAvailableTags] = useState([]);

  // Sync tags + available tags from context whenever:
  // - the modal opens
  // - the current file changes
  // - filteredData changes (e.g. after a tag operation)
  useEffect(() => {
    if (!open || !dataFile) return;

    console.log('CustomTagModal: syncing from context');

    // 1) Get the freshest version of this file from the context
    const updatedFile =
      filteredData.find((f) => f.filename === dataFile.filename) || dataFile;

    const currentTags = updatedFile.segmentation_data?.custom_tags || [];
    setTags(currentTags);

    // 2) Build global list of available tags from ALL files in filteredData
    const tagSet = new Set();
    filteredData.forEach((file) => {
      (file.segmentation_data?.custom_tags || []).forEach((t) => tagSet.add(t));
    });

    setLocalAvailableTags([...tagSet]);
  }, [open, dataFile, filteredData]);

  const getToken = () => localStorage.getItem('access_token');

  const handleTagOperation = async (url, method, body, onSuccess) => {
    console.log(`Starting ${method} operation for URL: ${url} with body:`, body);
    setIsProcessing(true);
  
    // If url is relative (e.g. "/api/add-tag"), prepend BACKEND_URL.
    // If it's already a full URL (starts with "http"), leave it as-is.
    const finalUrl = url.startsWith('http')
      ? url
      : `${BACKEND_URL}${url.startsWith('/') ? url : `/${url}`}`;
  
    try {
      const response = await fetch(finalUrl, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(body),
      });
  
      if (!response.ok) {
        console.error(`Failed ${method} operation at ${finalUrl}:`, response.status);
        return;
      }
  
      const responseData = await response.json();
      console.log('Response data from server:', responseData);
  
      const updatedFiles =
        responseData.updated_files || [responseData.updated_file];
  
      // Update the global context (DataAndFilterContext)
      updatedFiles.forEach((file) => {
        if (!file || !file.filename) {
          console.warn('Updated file missing filename:', file);
          return;
        }
        console.log('Updating context for file:', file.filename);
        updateDataFile(file.filename, file);
      });
  
      // Find the specific file we're editing in this modal
      const currentFile = updatedFiles.find(
        (f) => f && f.filename === dataFile.filename
      );
  
      if (currentFile) {
        const currentTags = currentFile.segmentation_data?.custom_tags || [];
        console.log('Current file found and updating tags:', currentFile);
  
        // Update the modal's local tag state so changes show immediately
        setTags(currentTags);
      } else {
        console.log('Current file not found in updated files');
      }
  
      if (onSuccess) {
        onSuccess(responseData);
      }
    } catch (error) {
      console.error(`Error during ${method} operation at ${finalUrl}:`, error);
    } finally {
      setIsProcessing(false);
    }
  };  

  const handleAddTag = (tag) => {
    console.log('Adding tag:', tag);
    handleTagOperation(
      '/api/add-tag',
      'POST',
      { filename: dataFile.filename, tag },
      () => {
        console.log('Tag added successfully (server + context updated)');
        // localAvailableTags will refresh via useEffect when filteredData changes
      }
    );
  };

  const handleRemoveTag = (tag) => {
    console.log('Removing tag:', tag);
    handleTagOperation(
      '/api/remove-tag',
      'POST',
      { filename: dataFile.filename, tag },
      () => {
        console.log('Tag removed successfully (server + context updated)');
      }
    );
  };

  const handleEditTag = (oldTag, newTagValue) => {
    if (!newTagValue.trim() || oldTag === newTagValue) return;
    console.log(`Editing tag from "${oldTag}" to "${newTagValue}"`);
    handleTagOperation(
      '/api/edit-tag',
      'POST',
      { old_tag: oldTag, new_tag: newTagValue },
      () => {
        console.log('Tag edited successfully (server + context updated)');
      }
    );
  };

  const handleDeleteTag = (tag) => {
    if (
      window.confirm(
        'Are you sure you want to delete this tag from all files that have this tag?'
      )
    ) {
      console.log('Deleting tag:', tag);
      handleTagOperation(
        '/api/delete-tag',
        'POST',
        { tag },
        () => {
          console.log('Tag deleted successfully (server + context updated)');
        }
      );
    }
  };

  const handleNewTagSubmit = (e) => {
    e.preventDefault();
    if (newTag.trim() && !tags.includes(newTag)) {
      console.log('Submitting new tag:', newTag);
      handleAddTag(newTag);
      setNewTag('');
    } else {
      console.log('New tag is either empty or already exists');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Custom Tags</DialogTitle>
      <DialogContent>
        <Box mb={2}>
          {localAvailableTags.map((tag, index) => (
            <Chip
              key={index}
              label={tag}
              onClick={() => {
                if (tags.includes(tag)) {
                  handleRemoveTag(tag);
                } else {
                  handleAddTag(tag);
                }
              }}
              onDelete={() => handleDeleteTag(tag)}
              icon={
                <EditIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    const newTagValue = prompt('Edit tag:', tag);
                    if (newTagValue) handleEditTag(tag, newTagValue);
                  }}
                />
              }
              color={tags.includes(tag) ? 'primary' : 'default'}
              clickable
              style={{ margin: '4px' }}
            />
          ))}
        </Box>
        <form onSubmit={handleNewTagSubmit}>
          <TextField
            label="New Tag"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            fullWidth
            margin="normal"
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isProcessing || !newTag.trim()}
          >
            {isProcessing ? <CircularProgress size={24} /> : 'Add Tag'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default CustomTagModal;
